<script setup>
definePage({ meta: { action: 'read', subject: 'subject' } })

import ConfirmDialogCustom from '@/components/dialogs/ConfirmDialogCustom.vue'
import { status } from '@/datas/subject'
import { getProjectId } from '@/utils/cdms'
import { isLockedProject } from '@/utils/project'
import { useESignatureStore } from '@/utils/stores/esignature'
import { useSurveyBuildStore } from '@/utils/stores/surveyBuild'
import { useProjectStore } from '@/utils/stores/project'
import { useAlertDialog } from '@/utils/stores/alertDialog'
import ESignature from "@/views/build/common/eSignature.vue"
import ReservationModal from "@/views/survey/ReservationModal.vue"
import ResponseResultModal from "@/views/survey/ResponseResultModal.vue"
import ConsentFormManager from "@/views/survey/ConsentFormManager.vue"
import SurveyPreview from "@/views/survey/SurveyPreview.vue"
import CrfGroupPage from "@/views/survey/crf-group-page/index.vue"
import CycleVisit from "@/views/survey/cycle-visit/index.vue"
import Schedule from "@/views/survey/schedule/index.vue"
import dayjs from 'dayjs'
import { toRaw } from 'vue'
import { VDataTable } from 'vuetify/labs/VDataTable'

const { t } = useI18n()
const esignatureStore = useESignatureStore()
const surveyBuildStore = useSurveyBuildStore()
const projectStore = useProjectStore()
const alertDialogStore = useAlertDialog()
const { isBuild: isSurveyBuild } = storeToRefs(surveyBuildStore)

const currentTab = ref(0)

// eCOA 빌드 관련
surveyBuildStore.isBuild = false

const loadGuardianBuild = async () => {
  const { data, error } = await useApi(createUrl('/guardian-build', {
    query: { projectId: projectStore.projectId },
  }))
  if (error.value) {
    console.error(error.value)
  } else if (data.value) {
    surveyBuildStore.crfGroup = data.value.crfGroup
    surveyBuildStore.cycleVisit = data.value.cycleVisit
    surveyBuildStore.schedule = data.value.schedule
    surveyBuildStore.uvSchedule = data.value.uvSchedule
    surveyBuildStore.hasConsent = !!data.value.hasConsent
  }
}

// ecoa 권한이 있을 때만 호출 (없으면 403 에러 토스트 방지)
const ability = useAbility()
if (ability.can('read', 'ecoa')) {
  loadGuardianBuild()
}

const toggleSurveyBuild = () => {
  esignatureStore.$reset()
  esignatureStore.isDialogVisible = true
}

const surveyApplyConfirmVisible = ref(false)

const onSurveyApplyClick = () => {
  surveyApplyConfirmVisible.value = true
}

const onSurveyApplyConfirm = (confirmed) => {
  if (confirmed) {
    toggleSurveyBuild()
  }
}

const surveySignConfirm = async ({ result }) => {
  if (!result) return
  if (surveyBuildStore.isBuild) {
    await doSaveGuardianBuild()
  } else {
    surveyBuildStore.isBuild = true
  }
}

async function doSaveGuardianBuild() {
  const body = {
    projectId: projectStore.projectId,
    crfGroup: surveyBuildStore.crfGroup,
    cycleVisit: surveyBuildStore.cycleVisit,
    schedule: surveyBuildStore.schedule,
    uvSchedule: surveyBuildStore.uvSchedule,
  }
  const res = await $api('/guardian-build', { method: 'POST', body })
  if (res?.code === 200) {
    surveyBuildStore.isBuild = false
    await loadGuardianBuild()
    alertDialogStore.showAlertDialog('저장되었습니다.')
  } else if (res?.message) {
    alertDialogStore.showAlertDialog(res.message, 'error')
  }
}

const page = ref(1)
const itemsPerPage = ref(10)

const isModalVisible = ref(false)
const selectedAnimal = ref(null)

const statusText = val => status.find(({ value }) => value == val)?.text
const projectLocked = ref(await isLockedProject())

const { data: subjectsData, execute: fetchSubjects } = await useApi(createUrl('/subjects', {
  query: { projectId: getProjectId() },
}))

const subjects = computed(() => subjectsData.value)

// ─────────────────────────────────────────────
// 검색 / 필터
// ─────────────────────────────────────────────
const search = ref('')

const filterItems = [
  { value: 'site_name',  title: '기관명' },
  { value: 'name',       title: '동물(검체)이름' },
  { value: 'subject_no', title: '식별번호' },
]

const filterItem = ref('name')

const filterText = (value, query, item) => {
  return value != null && query != null &&
    typeof value === 'string' &&
    item.columns[filterItem.value].toLowerCase().includes(query.toLowerCase())
}

watch(() => [search.value, filterItem.value], () => { page.value = 1 })

// subjects 초기화 시 전화번호 편집 상태 세팅
watch(subjects, () => {
  if (subjects.value) {
    subjects.value.forEach(item => {
      item.isEditingPhone = false
      item.editPhone = item.phone
    })
  }
})

function openReservationModal(animal) {
  selectedAnimal.value = animal
  isModalVisible.value = true
}

// ─────────────────────────────────────────────
// 전화번호 편집
// ─────────────────────────────────────────────
const isSnackbarVisible = ref(false)
const isPhoneSnackbarVisible = ref(false)

onMounted(() => { isSnackbarVisible.value = true })

function formatPhoneNumber(phone) {
  if (!phone) return ''
  const d = phone.replace(/\D/g, '')
  if (d.length === 11) return d.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3')
  if (d.length === 10) return d.replace(/(\d{3})(\d{3,4})(\d{4})/, '$1-$2-$3')
  
  return phone
}

async function savePhone(item) {
  const newPhone = item.editPhone
  try {
    const { error } = await $api(`/subjects/${item.id}/phone`, {
      method: 'PATCH',
      body: { phone: newPhone },
    })

    if (error?.value) throw new Error('서버 오류')
    item.phone = newPhone
    item.isEditingPhone = false
    isPhoneSnackbarVisible.value = true
  } catch (e) {
    console.error('전화번호 저장 실패:', e)
    item.editPhone = item.phone
    item.isEditingPhone = false
  }
}
function cancelPhoneEdit(item) {
  item.editPhone = item.phone
  item.isEditingPhone = false
}

// ─────────────────────────────────────────────
// 응답 결과 보기
// ─────────────────────────────────────────────
const isResponsesLayerVisible = ref(false)

function openResponseDetail(animal) {
  selectedAnimal.value = animal
  isResponsesLayerVisible.value = true
}


</script>

<template>
  <h3 class="text-lg font-semibold mb-8">
    보호자 설문
  </h3>

  <ConfirmDialogCustom
    v-model="surveyApplyConfirmVisible"
    confirmation-question="스케줄 등 모든 설정을 완료 하였습니까?"
    confirm-msg=""
    cancel-msg=""
    @confirm="onSurveyApplyConfirm"
  />
  <ESignature @confirm="surveySignConfirm" />

  <VRow>
    <VCol cols="12">
      <div class="d-flex align-center justify-space-between">
        <VTabs
          v-model="currentTab"
          class="v-tabs-pill"
        >
          <VTab>예약/응답 현황</VTab>
          <VTab v-if="$can('read', 'ecoa')">문서 개발</VTab>
          <VTab v-if="$can('read', 'ecoa')">Cycle & Day</VTab>
          <VTab v-if="$can('read', 'ecoa')">Schedule</VTab>
          <VTab v-if="$can('read', 'ecoa')">보호자동의서</VTab>
          <VTab v-if="$can('read', 'ecoa')">미리보기</VTab>
        </VTabs>

        <div v-if="currentTab >= 1 && currentTab <= 4 && $can('read', 'ecoa')">
          <VBtn
            v-if="isSurveyBuild"
            type="button"
            @click="onSurveyApplyClick"
          >
            적용
          </VBtn>
          <VBtn
            v-if="!isSurveyBuild && $can('write', 'surveys')"
            type="button"
            :disabled="projectLocked"
            @click="toggleSurveyBuild"
          >
            해제
            <VIcon
              v-if="projectLocked"
              end
              icon="tabler-lock"
            />
          </VBtn>
        </div>
      </div>

      <VWindow
        v-model="currentTab"
        class="disable-tab-transition"
      >
        <VWindowItem :value="0">
  <!-- 스낵바 -->
  <VSnackbar
    v-model="isSnackbarVisible"
    :timeout="2000"
    location="top"
  >
    처음 동물등록시 전화번호를 등록하고 메시지 예약 완료해주세요.
  </VSnackbar>
  <VSnackbar
    v-model="isPhoneSnackbarVisible"
    :timeout="2000"
    location="top"
  >
    전화번호가 저장되었습니다.
  </VSnackbar>
  <!-- 예약 모달 (분리된 컴포넌트) -->
  <ReservationModal
    v-model="isModalVisible"
    :animal="selectedAnimal"
    @saved="fetchSubjects"
  />

  <!-- 메인 표 -->
  <VCard class="v-card-content">
    <VCardText>
      <VDataTable
        :headers="[
          { title: '동물 이름', key: 'name' },
          { title: '식별번호', key: 'subject_no' },
          { title: '전화번호 (보호자)', key: 'phone', sortable: false },
          { title: '메시지 예약', key: 'sms', sortable: false },
          { title: '예약 현황', key: 'schedule_status', sortable: false },
          { title: '응답/발송 현황', key: 'answer_status', sortable: false },
          { title: '응답지', key: 'answer_action', sortable: false },
        ]"
        :items="subjects"
        :page="page"
        :items-per-page="itemsPerPage"
        class="elevation-1 custom-table"
        @update:page="val => page = val"
      >
        <template #item.name="{ item }">
          {{ item.name }}
        </template>
        <template #item.subject_no="{ item }">
          {{ item.subject_no }}
        </template>

        <!-- 전화번호 편집 -->
        <template #item.phone="{ item }">
          <div class="d-flex align-center">
            <div v-if="!item.isEditingPhone">
              <span>{{ formatPhoneNumber(item.phone) || '전화번호 없음' }}</span>
              <VBtn
                icon
                size="x-small"
                variant="text"
                @click="item.isEditingPhone = true"
              >
                <VIcon icon="tabler-edit" />
              </VBtn>
            </div>
            <div
              v-else
              class="d-flex align-center"
            >
              <VTextField
                v-model="item.editPhone"
                density="compact"
                hide-details
                style="inline-size: 140px"
                placeholder="전화번호 입력"
              />
              <VBtn
                icon
                size="x-small"
                variant="text"
                @click="savePhone(item)"
              >
                <VIcon icon="tabler-check" />
              </VBtn>
              <VBtn
                icon
                size="x-small"
                variant="text"
                @click="cancelPhoneEdit(item)"
              >
                <VIcon icon="tabler-x" />
              </VBtn>
            </div>
          </div>
        </template>

        <!-- 메시지 예약 관리 -->
        <template #item.sms="{ item }">
          <VBtn
            color="default"
            :class="item.phone ? 'blueBtn' : ''"
            :disabled="!item.phone"
            @click="openReservationModal(item)"
          >
            메시지 예약 관리
          </VBtn>
        </template>

        <!-- 예약/발송 현황 -->
        <template #item.schedule_status="{ item }">
          <template v-if="item.schedule_count > 0">
            예약완료 
          </template>
          <template v-else>
            예약 가능
          </template>
        </template>

        <!-- 응답 현황 -->
        <template #item.answer_status="{ item }">
          {{ item.answered_count }} /   {{ item.sent_count }}
        </template>

        <!-- 응답지 보기 → 레이어 팝업 -->
        <template #item.answer_action="{ item }">
          <VBtn
            color="primary"
            size="small"
            @click="openResponseDetail(item)"
          >
            응답 결과 보기
          </VBtn>
        </template>
      </VDataTable>
    </VCardText>
  </VCard>

  <!-- 응답 결과 보기 (새 버전) -->
  <ResponseResultModal
    v-model="isResponsesLayerVisible"
    :animal="selectedAnimal"
  />

        </VWindowItem>

        <VWindowItem :value="1">
          <CrfGroupPage />
        </VWindowItem>
        <VWindowItem :value="2">
          <CycleVisit />
        </VWindowItem>
        <VWindowItem :value="3">
          <Schedule />
        </VWindowItem>
        <VWindowItem :value="4">
          <ConsentFormManager />
        </VWindowItem>
        <VWindowItem :value="5">
          <SurveyPreview />
        </VWindowItem>
      </VWindow>
    </VCol>
  </VRow>
</template>

<style lang="scss" scoped>
/* 테이블 스타일 */
.v-data-table .v-table__wrapper > table > thead > tr > th,
.v-data-table .v-table__wrapper > table > tbody > tr > th { font-size: 15px; }

.elevation-1 .v-data-table .v-table__wrapper > table > thead > tr > th {
  background-color: #f8f8f8; font-size: 15px; font-weight: 600;
}

.v-card-content{ margin-block-start: 20px; }
.blueBtn{ background:#E7E6FD; color:#7367F0; }

:deep(.custom-table .v-table__wrapper table) {
  th {
    background:#F3F5F8 !important; border-block-end:1px solid #eee;
    color:#909090 !important; font-size:13px; font-weight:500;
    padding-block:10px; padding-inline:12px;
  }

  td {
    border-block-end:1px solid #E8EBEE; color:#444; font-size:14px;
    padding-block:8px; padding-inline:12px;
  }
  tr:hover td { background-color:#f9f9fc; }
}

/* 응답 레이어 레이아웃 */
.responses-layer{
  display: grid;
  gap: 16px;
  grid-template-columns: 56% 44%;
  min-block-size: 520px;
}

.resp-list{
  overflow: hidden;
  border: 1px solid #E8EBEE;
  border-radius: 12px;
  background: #fff;
}

.resp-list .list-head{
  background: #F3F5F8;
  border-block-end: 1px solid #E8EBEE;
  color: #6b6f76;
  font-weight: 600;
  padding-block: 10px;
  padding-inline: 12px;
}
.resp-list .list-scroll{ overflow: auto; max-block-size: 520px; }
.resp-item{ cursor: pointer; padding-block: 10px; padding-inline: 12px; }
.resp-item.active{ background: #F6F7FF; border-inline-start: 4px solid #7367F0; }
.resp-item .day{ font-weight: 600; }
.resp-item .date{ color: #8b8f96; font-size: 12px; margin-block-start: 2px; }

.resp-edit{
  padding: 14px;
  border: 1px solid #E8EBEE;
  border-radius: 12px;
  background: #fff;
}
.edit-head{ display:flex; align-items:center; justify-content:space-between; margin-block-end: 8px; }
.edit-head .title{ font-weight: 700; }
.edit-head .sub{ color:#8b8f96; font-size: 12px; }

.slider-row{ display:flex; align-items:center; gap: 16px; margin-block-start: 16px; }

/* 예약 모달 */
.deleted-row { opacity: .6; text-decoration: line-through; }
.text-subtitle-2{ font-weight: bold; margin-block-start: 10px; }

/* 탭 스타일 */
.v-tabs.v-tabs-pill {
  .v-slide-group-item--active.v-tab--selected {
    background-color: #6A78D3;
    color: white;
    font-weight: bold;
  }
}

.v-tabs.v-tabs-pill .v-tab.v-btn{
  border : 1px solid #6A78D3;
  color: #2F45B8;
  margin-inline-end: 10px;
}
</style>
